import {computed, inject, Injectable, signal} from '@angular/core'
import {toObservable} from '@angular/core/rxjs-interop'
import {catchError, map, Observable, of, switchMap, take, tap} from 'rxjs'

import {ContextService} from './context.service'
import {UserDataService} from './user-data.service'

export interface UserFeatures {
  disableForcePush: boolean
}

export const DEFAULT_USER_FEATURES: UserFeatures = {
  disableForcePush: false,
}

const FEATURES_KEY = 'features'

export function parseFeatures(raw: unknown): UserFeatures {
  if (typeof raw !== 'string' || !raw) {
    return {...DEFAULT_USER_FEATURES}
  }
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(raw) as Record<string, unknown>
  } catch {
    return {...DEFAULT_USER_FEATURES}
  }
  const value = parsed['disableForcePush']
  return {disableForcePush: value === true || value === 'true'}
}

function stringifyFeatures(features: UserFeatures): string {
  return JSON.stringify({
    disableForcePush: features.disableForcePush ? 'true' : 'false',
  })
}

@Injectable({providedIn: 'root'})
export class UserFeaturesService {
  private readonly userData = inject(UserDataService)
  private readonly context = inject(ContextService)
  private readonly _current = signal<UserFeatures>({...DEFAULT_USER_FEATURES})

  readonly current = computed(() => this._current())
  readonly current$: Observable<UserFeatures> = toObservable(this._current)

  loadForCurrentUser(): Observable<UserFeatures> {
    return this.userData.getUserDataBasedOnContext().pipe(
      map((data) => parseFeatures(data?.userData?.[FEATURES_KEY])),
      tap((features) => this._current.set(features)),
      catchError(() => {
        this._current.set({...DEFAULT_USER_FEATURES})
        return of({...DEFAULT_USER_FEATURES})
      }),
    )
  }

  loadForUser(
    domain: string,
    userId: string | number,
  ): Observable<UserFeatures> {
    return this.userData.getUserData(domain, userId).pipe(
      take(1),
      map((data) => parseFeatures(data?.userData?.[FEATURES_KEY])),
      catchError(() => of({...DEFAULT_USER_FEATURES})),
    )
  }

  updateForUser<K extends keyof UserFeatures>(
    domain: string,
    userId: string | number,
    key: K,
    value: UserFeatures[K],
  ): Observable<UserFeatures> {
    return this.userData.getUserData(domain, userId).pipe(
      take(1),
      switchMap((data) => {
        const previous = parseFeatures(data?.userData?.[FEATURES_KEY])
        const next: UserFeatures = {...previous, [key]: value}
        return this.userData
          .setUserData(domain, userId, {
            ...data?.userData,
            [FEATURES_KEY]: stringifyFeatures(next),
          })
          .pipe(map(() => next))
      }),
      tap((next) => {
        if (this.isCurrentUser(domain, userId)) {
          this._current.set(next)
        }
      }),
    )
  }

  isEnabled(key: keyof UserFeatures): boolean {
    return !!this._current()[key]
  }

  resetForLogout(): void {
    this._current.set({...DEFAULT_USER_FEATURES})
  }

  private isCurrentUser(domain: string, userId: string | number): boolean {
    const currentDomain = this.context.loginDomain
    const currentId = this.context.userId
    if (!currentDomain || currentId == null) {
      return false
    }
    return (
      String(currentDomain) === String(domain) &&
      String(currentId) === String(userId)
    )
  }
}
